import React, { useEffect, useState } from "react";
import { Tooltip } from "antd";
import { toDecimalInput } from "./itemDraftHelpers";
import { density } from "./itemMeasurePreview";

const LB_TO_KG = 0.453592;

function lbToKg(lb) {
  return (parseFloat(lb) || 0) * LB_TO_KG;
}

function fmt(value, decimals = 2) {
  const num = parseFloat(value);
  if (value === null || value === undefined || Number.isNaN(num)) {
    return (0).toFixed(decimals);
  }
  return num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// Mirrors the mockup's "-.--" placeholder for Re-Weight/Re-Dim WT until the
// backend has actual reweigh data for every unit (summary.reweigh_*_lb is
// null until then).
function fmtNullable(value, unit) {
  if (value === null || value === undefined) {
    return `-.-- ${unit}`;
  }
  return `${fmt(value)} ${unit}`;
}

// 8 metric tiles per mockup `.summary` grid. `summary` is the API's summary
// object (null pre-save); every value defaults to a safe zero/placeholder so
// the tiles render before the order has any units.
const SummaryBar = ({
  summary,
  declaredValue = "",
  onDeclaredValueSave,
  // Optional live-capture (create flow): fires on every keystroke so the
  // parent can stash the value before any blur/save.
  onValueChange,
  // Org config: hide the Declared Value tile entirely when the org opted out.
  hideDeclaredValue = false,
  readOnly = false,
  saveDisabledReason = null,
  volumeFt3 = 0,
  dimFactor,
  // B3 (mockup-parity): { pcs, hus, volumeFt3 } computed from the container's
  // live item DRAFTS while any item draft is dirty, null once clean — see
  // ItemsMeasureUnits.jsx's computeDraftTotals. Quantity/Volume are the only
  // tiles that go stale mid-edit (both are pure functions of fields the
  // ItemCard form edits directly); every other tile only reconciles from
  // server-derived rollups (reweigh sums, chargeable weight, ...) that a
  // draft alone can't recompute, so they intentionally stay server-sourced
  // even while dirty.
  draftTotals = null,
  // Live + partial scannable-unit rollups (see computeLiveUnitTotals). When
  // present, Booked/Re-Weight/Re-Dim/Chargeable reflect the on-screen units
  // (partial) instead of waiting for the all-units server rollup. draftTotals
  // (item-level edits) still wins for Booked weight when an item is dirty.
  liveUnitTotals = null,
  // True while the user is actively editing unit values — gates whether Booked
  // weight/dim follow the live unit sum vs. the authoritative order total on
  // load.
  hasLiveUnitEdits = false,
}) => {
  const [value, setValue] = useState(declaredValue ?? "");

  useEffect(() => {
    setValue(declaredValue ?? "");
  }, [declaredValue]);

  const inputDisabled = readOnly || Boolean(saveDisabledReason);

  const handleBlur = () => {
    if (inputDisabled) return;
    // Dirty-check against the last server-confirmed value (string-compared as
    // typed) so tabbing through the field never fires a write.
    if (String(value) === String(declaredValue ?? "")) return;
    if (typeof onDeclaredValueSave !== "function") return;
    Promise.resolve(onDeclaredValueSave(value)).then((result) => {
      // Standard truthy check used across this tree: on failure
      // ParseGeneralResponse swaps the result for HttpErrorMessage output,
      // which has NO success key at all — `success === false` never matches.
      if (result && result.success) {
        if (result.declared_value !== undefined && result.declared_value !== null) {
          // Sync to the server's canonical (rounded) value even when the
          // declaredValue prop doesn't change (e.g. "123.4600" -> 123.46).
          setValue(String(result.declared_value));
        }
      } else {
        // Failed save (container already toasts the error envelope — no
        // double-toast here): revert the input to the last persisted value
        // so the screen never shows a number the server doesn't have.
        setValue(declaredValue ?? "");
      }
    });
  };

  // Weight / Dim WT reflect the live sum of the item drafts while editing
  // (or entering a new order), same as Quantity/Volume — the top section
  // mirrors the items on screen, not just the last saved server rollup.
  // Booked weight/dim = the ORDER/item total. Item-level draft edits win
  // (draftTotals, so the tiles track the item form live). Otherwise, once the
  // user starts editing UNIT values, follow the live unit sum so unit edits
  // reflect too; on a freshly-loaded order (no live edits) show the
  // authoritative server total. Not the unit sum by default, since that could
  // read stale/zero on load.
  const weightLb = draftTotals
    ? draftTotals.weightLb
    : hasLiveUnitEdits && liveUnitTotals
    ? liveUnitTotals.bookedWeightLb
    // Server is authoritative once there's no live/draft preview to show —
    // the write response (and the reload backstop) both carry the fresh
    // order rollup now, so there's no more client-side items sum to fall
    // back to.
    : summary?.weight_lb ?? 0;
  // DIM WT = the live sum of the units' booked dim weights whenever it's
  // computable. That rollup already reflects BOTH item-dim edits (the create /
  // preview seed units derive their dims from the item draft) AND per-unit dim
  // edits, in create, inline edit and entire update — so it wins over the
  // item-draft figure and the server total here. Falls back to the item-draft
  // dim weight, then the server rollup. `?? null` (not `|| 0`) keeps a
  // no-Dim-config account rendering "—" via fmtNullable instead of 0, and keeps
  // Math.max below 0-safe (null) rather than NaN (undefined).
  const dimWeightLb =
    liveUnitTotals && liveUnitTotals.bookedDimLb !== null
      ? liveUnitTotals.bookedDimLb
      : draftTotals
      ? draftTotals.dimWeightLb
      : summary?.dim_weight_lb ?? null;
  // Re-Weight / Re-Dim: partial live unit sums when units exist (reflect unit
  // edits as they're typed), else the (all-units) server rollup.
  const reweighWeightLb = liveUnitTotals ? liveUnitTotals.reweighWeightLb : summary?.reweigh_weight_lb ?? null;
  const reweighDimWeightLb = liveUnitTotals ? liveUnitTotals.reweighDimLb : summary?.reweigh_dim_weight_lb ?? null;
  // Chargeable = max(weight side, dim side); each side prefers its reweigh
  // value when present. Recomputed live whenever the user is editing item
  // drafts (incl. the create flow) or any units exist, so it tracks both item
  // and unit edits; falls back to the server value on a pure read-only load.
  const chargeableWeightLb =
    draftTotals || liveUnitTotals
      ? Math.max(
          reweighWeightLb !== null && reweighWeightLb !== undefined ? reweighWeightLb : weightLb,
          reweighDimWeightLb !== null && reweighDimWeightLb !== undefined ? reweighDimWeightLb : dimWeightLb
        )
      : summary?.chargeable_weight_lb ?? 0;
  const quantityHus = draftTotals ? draftTotals.hus : summary?.quantity_hus ?? 0;
  const quantityPcs = draftTotals ? draftTotals.pcs : summary?.quantity_pcs ?? 0;
  const effectiveVolumeFt3 = draftTotals ? draftTotals.volumeFt3 : volumeFt3;
  const volumeM3 = (parseFloat(effectiveVolumeFt3) || 0) * 0.0283168;
  // PCF / KCM = freight density = ACTUAL weight per unit volume, so they track
  // both weight and dims (volume) as they change.
  //   PCF = weight (lb) / volume (ft³)
  //   KCM = weight (kg) / volume (m³)
  // Bugfix: these previously divided the DIM weight by volume, but dim weight
  // is itself proportional to volume ((L×W×H/factor)×HUs vs (L×W×H×HUs)/1728),
  // so the ratio collapsed to a constant 1728/factor and never moved when the
  // user edited dims (and read 0 in the create flow with no factor) — the
  // reported "PCF/KCM don't update with m³/ft³". Use the actual weight instead
  // (reweigh/measured weight when present, else booked). Guarded against a
  // zero/empty volume so they read "0.00" not Infinity.
  const densityWeightLb = reweighWeightLb !== null && reweighWeightLb !== undefined
    ? reweighWeightLb
    : weightLb;
  // Task 5: pcf/kcm now come from the shared itemMeasurePreview.density(),
  // same formula (and the same 0.1 ft³ ≈ 5-inch-cube threshold below which
  // weight ÷ ~0 would otherwise explode into absurd figures) — both null when
  // volume isn't meaningful yet, so the tiles below render "—" instead of a
  // misleading "0.00".
  const { pcf, kcm } = density(densityWeightLb, effectiveVolumeFt3);
  const densityAvailable = pcf !== null && kcm !== null;

  // The strip is a fixed 8-tile grid; when the org hides the Dec. Value tile
  // only 7 render, so drop the grid to 7 columns too — otherwise the 8th
  // column stays empty and the remaining tiles keep their narrow 1/8 width
  // instead of spreading to fill the row.
  const visibleTileCount = hideDeclaredValue ? 7 : 8;

  return (
    <div
      className="summary"
      style={{ gridTemplateColumns: `repeat(${visibleTileCount}, 1fr)` }}
    >
      <div className="metric">
        <div className="label">
          Weight
        </div>
        <div className="value">{fmt(weightLb)} lb</div>
        <div className="value-sub">{fmt(lbToKg(weightLb))} kg</div>
      </div>
      <div className="metric">
        <div className="label">
          Dim WT
        </div>
        <div className="value">{fmtNullable(dimWeightLb, "lb")}</div>
        <div className="value-sub">
          {fmtNullable(
            dimWeightLb === null || dimWeightLb === undefined ? null : lbToKg(dimWeightLb),
            "kg"
          )}
        </div>
      </div>
      <div className="metric">
        <div className="label">Re-Weight</div>
        <div className="value">{fmtNullable(reweighWeightLb, "lb")}</div>
        <div className="value-sub">
          {fmtNullable(
            reweighWeightLb === null ? null : lbToKg(reweighWeightLb),
            "kg"
          )}
        </div>
      </div>
      <div className="metric">
        <div className="label">
          Re-Dim WT
        </div>
        <div className="value">{fmtNullable(reweighDimWeightLb, "lb")}</div>
        <div className="value-sub">
          {fmtNullable(
            reweighDimWeightLb === null ? null : lbToKg(reweighDimWeightLb),
            "kg"
          )}
        </div>
      </div>
      <div className="metric">
        <div className="label">Chargeable WT</div>
        <div className="value">{fmt(chargeableWeightLb)} lb</div>
        <div className="value-sub">{fmt(lbToKg(chargeableWeightLb))} kg</div>
      </div>
      <div className="metric">
        <div className="label">
          Quantity
        </div>
        <div className="value">{quantityHus} HUs</div>
        <div className="value-sub">{quantityPcs} pcs</div>
      </div>
      <div className="metric">
        <div className="label">
          Volume
        </div>
        <div className="value value--dual">
          {fmt(volumeM3)} m&sup3; &middot; {fmt(effectiveVolumeFt3)} ft&sup3;
        </div>
        <div className="value-sub value--dual">
          {densityAvailable ? fmt(kcm) : "—"} KCM &middot;{" "}
          {densityAvailable ? fmt(pcf) : "—"} PCF
        </div>
      </div>
      {!hideDeclaredValue && (
      <div className="metric">
        <div className="label">Dec. Value</div>
        <Tooltip title={!readOnly && saveDisabledReason ? saveDisabledReason : ""}>
          <div className="dollar-input">
            <span>$</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={value}
              disabled={inputDisabled}
              onChange={(e) => {
                const next = toDecimalInput(e.target.value);
                setValue(next);
                if (typeof onValueChange === "function") onValueChange(next);
              }}
              onBlur={handleBlur}
            />
          </div>
        </Tooltip>
      </div>
      )}
    </div>
  );
};

export default SummaryBar;
